import { computed, defineComponent, nextTick, PropType, ref, watch } from "vue";
import StepBox from "../../components/StepBox";
import InputText from "../../forms/InputText";
import Utils from "../../utils/Utils";
import InputCheckbox from "../../forms/InputCheckbox";
import Validator, { ValidationError } from "../../utils/Validator";
import InputRadio from "../../forms/InputRadio";
import InputSelect from "../../forms/InputSelect";
import Expander from "../../components/Expander";
import OfferUtils from "../../utils/OfferUtils";
import Tracking from "../../utils/Tracking";
import { SmeOrderJson, SmeRepresentative } from "../../utils/SmeUtils";


const InputBigCheck = defineComponent({
    components: {
        InputCheckbox,
    },

    template: `
        <div class="InputBigCheck">
            <div class="InputBigCheck__inner">
                <div class="InputBigCheck__input">
                    <InputCheckbox :title="title" :single="single" :modelValue="modelValue" @update:modelValue="$emit('update:modelValue', $event)" />
                </div>

                <div v-if="info" class="InputBigCheck__info" v-html="info"></div>
            </div>
        </div>
    `,

    emits: [
        'update:modelValue',
    ],

    props: {
        modelValue: { type: [Number, Boolean], require: true },
        single: { type: Boolean, default: true },

        title: { type: String, require: true },
        info: { type: String },
    },
});

const InputSmeRepresentative = defineComponent({
    components: {
        InputText,
        InputCheckbox,
    },

    template: `
        <div class="InputSmeRepresentative">
            <InputText label='<span class="requiredfield">*</span>Imię i nazwisko osoby podpisującej umowę' :modelValue="modelValue.name" @update:modelValue="onUpdate('name', $event)" :required="true" :error="errors.name" />
        </div>
    `,

    emits: [
        'update:modelValue',
    ],

    props: {
        modelValue: { type: Object as PropType<SmeRepresentative>, require: true },

        sent: { type: Boolean },
    },

    setup(props, ctx) {
        const errors = ref({
            name: computed(() => inputError('name', props.sent)),
        });

        function assertError(field: string, force: boolean = false) {
            field == 'name' && (force || props.modelValue.name) && Validator.assertNames(props.modelValue.name);
        }

        function inputError(field: string, force: boolean = false) {
            try {
                assertError(field, force);
            } catch (e) {
                if (e instanceof ValidationError) {
                    return e.message;
                }

                return 'Błędny format pola';
            }
        }

        function assertValid() {
            assertError('name', true);
        }

        async function onUpdate(field: string, value) {
            try {
                const el = props.modelValue;

                switch (field) {
                    case 'name':
                        el.name = value;
                        break;
                }

                ctx.emit('update:modelValue');
            } catch (e) {
            }
        }

        return {
            errors,

            assertValid,
            onUpdate,
        }
    },
});

export default defineComponent({
    components: {
        InputText,
        InputCheckbox,
        InputRadio,
        InputSelect,
        StepBox,
        Expander,
        InputBigCheck,
        InputSmeRepresentative,
    },

    template: `
        <StepBox class="StepSmeFormContents StepFormContents"
            :step="3"
            :currentstep="step"
            title="Dane osobowe i zgody"

            sticky="auto"

            :valid="true"
            :error="sent ? options.valid_consents_error : null"

            @edit="$emit('goto', 3)"
            @back="$emit('goto', 2)"
            @next="onNext"
        >
            <div class="StepSmeFormConsents__form StepFormContents__form">
                <h3>Dane reprezentanta</h3>

                <InputText label='<span class="requiredfield">*</span>Numer telefonu osoby podpisującej umowę' v-model="form.phone" :required="true" :error="errors.phone" />
                <InputText label='<span class="requiredfield">*</span>Adres e-mail osoby podpisującej umowę' v-model="form.email" :required="true" :error="errors.email" />

                <InputSmeRepresentative
                    ref="reps"
                    v-for="r in form.representatives"
                    v-model="r"

                    :sent="errors_active"
                />

                <InputCheckbox title='<span class="requiredfield">*</span>Oświadczam, że jestem umocowany/umocowana do reprezentowania danego podmiotu' v-model="form.terms_person" :error="errors.terms_person" />

                <button class="StepSmeFormConsents__addrepresentative" type="button" @click="addRepresentative">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M19.5397 5.34969C19.1897 5.61969 19.1197 6.11969 19.3797 6.46969C20.5897 8.06969 21.2197 9.98969 21.2197 11.9997C21.2197 17.0797 17.0797 21.2197 11.9997 21.2197C6.91969 21.2197 2.77969 17.0797 2.77969 11.9997C2.77969 6.91969 6.91969 2.77969 11.9997 2.77969C13.8497 2.77969 15.6297 3.31969 17.1497 4.34969C17.5197 4.59969 18.0097 4.49969 18.2597 4.13969C18.5097 3.76969 18.4097 3.27969 18.0397 3.02969C16.2497 1.81969 14.1597 1.17969 11.9897 1.17969C6.02969 1.17969 1.17969 6.02969 1.17969 11.9997C1.17969 17.9697 6.02969 22.8197 11.9997 22.8197C17.9697 22.8197 22.8197 17.9697 22.8197 11.9997C22.8197 9.63969 22.0697 7.38969 20.6597 5.50969C20.3997 5.15969 19.8897 5.08969 19.5397 5.34969Z" fill="#005537"/>
                        <path d="M12.0004 18.8104C12.4404 18.8104 12.8004 18.4504 12.8004 18.0104V12.8004H18.0104C18.4504 12.8004 18.8104 12.4404 18.8104 12.0004C18.8104 11.5604 18.4504 11.2004 18.0104 11.2004H12.8004V5.99043C12.8004 5.55043 12.4404 5.19043 12.0004 5.19043C11.5604 5.19043 11.2004 5.55043 11.2004 5.99043V11.2004H5.99043C5.55043 11.2004 5.19043 11.5604 5.19043 12.0004C5.19043 12.4404 5.55043 12.8004 5.99043 12.8004H11.2004V18.0104C11.2004 18.4504 11.5604 18.8104 12.0004 18.8104Z" fill="#005537"/>
                    </svg>

                    Dodaj kolejnego reprezentanta
                </button>

                <hr>

                <h3>Zgody</h3>

                <p>Poniżej znajdziesz zgody i oświadczenia, o których wyrażenie Cię prosimy. Zgody obowiązkowe są niezbędne do realizacji zamówienia. Zapoznaj się z ich treścią, zanim je zaakceptujesz.</p>

                <h4>Zgody, dzięki którym sfinalizujesz zamówienie</h4>

                <InputCheckbox :title="label_terms_rules" v-model="form.terms_rules" :error="errors.terms_rules" />
                <InputCheckbox title='<span class="requiredfield">*</span>Chcę otrzymywać mailem informacje o mojej umowie i zmianach do umowy' v-model="form.terms_contract" :error="errors.terms_contract" />

                <hr>

                <h4>Zgody, dzięki którym obniżysz opłatę handlową</h4>
                <InputCheckbox title="Wyrażam zgodę na e-Fakturę" v-model="form.terms_einvoice" />

                <div class="StepFormContents__sub">
                    <p v-if="options.fees?.einvoice_netto" class="StepFormContents__legend">Wyrażając zgodę na e-fakturę, <b>opłata handlowa zostanie pomniejszona o {{ priceFormat(options.fees?.einvoice_netto) }}</b>.</p>
                </div>

                <InputCheckbox title="Wyrażam zgodę na otrzymywanie od Respect Energy S.A informacji dotyczących produktów i usług Respect Energy S.A za pośrednictwem:" :modelValue="terms_trade > 0" :single="terms_trade == 3" @update:modelValue="onTerms('terms_trade')" />

                <div class="StepFormContents__sub">
                    <div class="StepFormContents__termslast-group">
                        <InputCheckbox title="e-mail" v-model="form.terms_trade_email" />
                        <InputCheckbox title="SMS" v-model="form.terms_trade_sms" />
                        <InputCheckbox title="telefon" v-model="form.terms_trade_phone" />
                    </div>

                    <p v-if="options.fees?.trade_netto" class="StepFormContents__legend">Wyrażając wszystkie zgody na komunikację marketingową, <b>opłata handlowa zostanie pomniejszona o kolejne {{ priceFormat(options.fees?.trade_netto) }}</b></p>
                </div>

                <hr>

                <h4>Zgoda, dzięki której przyspieszysz realizację zamówienia</h4>

                <InputCheckbox class="StepFormContents__boldcheck" title="Chcę korzystać z usług Respect Energy jak najszybciej po podpisaniu umowy" v-model="form.terms_notice" />

                <div class="StepFormContents__sub">
                    <p class="StepFormContents__legend">Wyrażam zgodę na natychmiastowe rozpoczęcie usług i przyjmuję do wiadomości utratę prawa do odstąpienia od umowy zgodnie z art. 38 ustawy o prawach konsumenta. Sytuacja dotyczy jedynie osób prowadzących Jednoosobowe Działalności Gospodarcze.</p>
                </div>

                <p class="StepFormContents__required">Pola oznaczone czerwoną gwiazdką * są wymagane</p>
            </div>
        </StepBox>
    `,

    emits: [
        'next',
        'goto',
    ],

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
        step: { type: Number, required: true },
    },

    setup(props, ctx) {
        const reps = ref(null);

        const errors_active = ref(false);
        const sent = ref(false);

        const form = ref({
            email: props.options.email,
            phone: props.options.phone,
            terms_person: props.options.terms_person ? 1 : 0,

            representatives: props.options.representatives,

            terms_rules: props.options.terms ? 1 : 0,
            terms_trade_email: props.options.terms_trade_email ? 1 : 0,
            terms_trade_sms: props.options.terms_trade_sms ? 1 : 0,
            terms_trade_phone: props.options.terms_trade_phone ? 1 : 0,

            terms_einvoice: props.options.terms_einvoice ? 1 : 0,
            terms_contract: props.options.terms_contract ? 1 : 0,
            terms_notice: props.options.terms_notice ? 1 : 0,
        });

        const errors = ref({
            phone: computed(() => inputError('phone', errors_active.value)),
            email: computed(() => inputError('email', errors_active.value)),
            terms_person: computed(() => inputError('terms_person', errors_active.value)),
            terms_rules: computed(() => inputError('terms_rules', errors_active.value)),
            terms_contract: computed(() => inputError('terms_contract', errors_active.value)),
        });

        const terms_all = computed(() => {
            return (terms_trade.value == 3 ? 1 : 0) + (terms_required_all.value == 2 ? 1 : 0);
        })

        const terms_trade = computed(() => {
            return terms_trade_all.value;
        })

        const terms_trade_all = computed(() => {
            return form.value.terms_trade_email + form.value.terms_trade_sms + form.value.terms_trade_phone;
        })

        const terms_required_all = computed(() => {
            return form.value.terms_rules + form.value.terms_contract;
        })

        const label_terms_rules = computed(() => {
            const r = ['<span class="requiredfield">*</span>Akceptuję'];

            const codeitem = props.options.code ? OfferUtils.getOfferCodeItem(props.options.code, props.options.offer?.id) : null;
            const terms = OfferUtils.getOfferTerms(props.options.offer, codeitem);

            for (const term of terms) {
                r.push(`<a href="${term.url}" target="_blank">${term.title}</a>`);
            }

            return r.join(', ');
        })

        function assertError(field: string, force: boolean = false) {
            if (field == 'representatives') {

            }

            field == 'phone' && (force || form.value.phone) && Validator.assertPhone(form.value.phone);
            field == 'email' && (force || form.value.email) && Validator.assertEmail(form.value.email);

            if (field == 'terms_person' && force && !form.value.terms_person) {
                throw new ValidationError('To pole jest wymagane');
            }

            field == 'terms_rules' && force && Validator.assertRequired(form.value.terms_rules);
            field == 'terms_contract' && force && Validator.assertRequired(form.value.terms_contract);
        }

        function inputError(field: string, force: boolean = false) {
            try {
                assertError(field, force);
            } catch (e) {
                if (e instanceof ValidationError) {
                    return e.message;
                }

                return 'Błędny format pola';
            }
        }

        function assertValid() {
            assertError('phone', true);
            assertError('email', true);
            assertError('representatives', true);
            assertError('terms_person', true);
            assertError('terms_rules', true);
            assertError('terms_contract', true);
        }

        function priceFormat(val: number) {
            return Utils.priceFormat(val, false, ' zł', 2);
        }

        function addRepresentative() {
            form.value.representatives.push({
                name: null,
            });
        }

        function onTerms(key: string, v: any = null): void {
            switch (key) {
                case 'terms_all':
                    if (v === null) {
                        v = !terms_all.value;
                    }

                    onTerms('terms_trade', v);
                    onTerms('terms_required_all', v);
                    break;

                case 'terms_trade':
                    if (v === null) {
                        v = !terms_trade_all.value;
                    }

                    onTerms('terms_trade_all', v);
                    break;

                case 'terms_trade_all':
                    if (v === null) {
                        v = !terms_trade_all.value;
                    }

                    form.value.terms_trade_email = v;
                    form.value.terms_trade_sms = v;
                    form.value.terms_trade_phone = v;
                    break;

                case 'terms_required_all':
                    if (v === null) {
                        v = !terms_required_all.value;
                    }

                    onTerms('terms_rules', v);
                    onTerms('terms_contract', v);
                    break;

                case 'terms_rules':
                    if (v === null) {
                        v = !form.value.terms_rules;
                    }

                    form.value.terms_rules = v;
                    break;

                case 'terms_einvoice':
                    if (v === null) {
                        v = !form.value.terms_einvoice;
                    }

                    form.value.terms_einvoice = v;
                    break;

                case 'terms_contract':
                    if (v === null) {
                        v = !form.value.terms_contract;
                    }

                    form.value.terms_contract = v;
                    break;

                case 'terms_notice':
                    if (v === null) {
                        v = !form.value.terms_notice;
                    }

                    form.value.terms_notice = v;
                    break;
            }
        }

        async function onNext() {
            errors_active.value = true;

            try {
                for (const rep of reps.value) {
                    rep.assertValid();
                }

                assertValid();

                await Utils.setCart('form.consents', {
                    form: form.value,
                });

                Tracking.doEvent('b2bsme_companydata_step3');

                sent.value = true;

                ctx.emit('next');
            } catch (e) {
                nextTick(() => {
                    const $first = $('.InputWidget--error, .InputCheckbox--error').first();
                    Utils.scrollToEl($first);
                });
            }
        }

        // watch(props.options, () => {
        //     setData();
        // });

        // setData();

        return {
            reps,

            errors_active,
            sent,
            form,
            errors,

            terms_all,
            terms_trade,
            terms_trade_all,
            terms_required_all,
            label_terms_rules,

            priceFormat,
            addRepresentative,
            onTerms,
            onNext,
        };
    },
});
